import { action, observable, makeObservable, runInAction } from "mobx";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { mrbDefaultFormats, currencyFormatter } from "mrb/common/formatting";
import { ProducerPayoutUserEventFilter } from "administration/producer/filters";
import { UserEventPayoutCell } from "common/components/producer";
import { UserPayoutEventListContentCell } from "administration/analytics/components";
import { applicationDefaults } from "common/constants";
import { convertSecondsToHMS } from "common/utils";
import { find, findIndex, forEach, isNil } from "lodash";

class ProducerPayoutUserEventListViewStore extends MrbBaseListViewStore {
    @observable.ref user = null;
    @observable isCollapsed = false;
    producerId = null;
    selectedReportViewId = 0;

    itemDeviceIds = [];

    @observable.ref trackListenerGridStore = null;
    @observable.ref trackGridStore = null;

    constructor(rootStore, { item, producerId, routeStore }) {
        super(rootStore, {
            queryConfig: {
                ignoreQueryParams: true,
                disableUpdateQueryParams: true,
                filter: new ProducerPayoutUserEventFilter(),
                embed: "producer,device,station,podcast,nakiNigunAlbum,shuffle",
                orderBy: "timeStamp",
                orderDirection: "desc",
            },
        });
        makeObservable(this);
        this.routeStore = routeStore;
        this.item = item;
        this.producerId = producerId;

        this.producerReportStore =
            this.rootStore.producerPaymentReportPreviewViewStore ?? this.rootStore.producerRevenueReportViewStore;
        this.selectedReportViewId = this.producerReportStore.selectedReportViewId;

        this.setGridStore(
            new MrbTableViewStore(this.rootStore, this.queryUtility, {
                columns: [
                    {
                        key: "device.deviceName",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.DEVICE",
                    },
                    {
                        key: "producer.name",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.PRODUCER",
                    },
                    {
                        key: "",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.CONTENT_NAME",
                        cell: UserPayoutEventListContentCell,
                    },
                    {
                        key: "trackTitle",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.TRACK",
                    },
                    {
                        key: "trackDuration",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.TRACK_DURATION",
                        format: (value) => convertSecondsToHMS(value),
                    },
                    {
                        key: "isAllowedForPayout",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.COLLECTED",
                        cell: UserEventPayoutCell,
                    },
                    {
                        key: "durationForPayout",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.DURATION_FOR_PAYOUT",
                        format: (value) => convertSecondsToHMS(value),
                    },
                    {
                        key: "durationForPayout",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.POINTS_FOR_PAYOUT",
                    },
                    {
                        key: "timeStamp",
                        title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.TIMESTAMP",
                        sortable: true,
                        format: {
                            type: mrbDefaultFormats.date,
                            format: applicationDefaults.formats.dateTimeSeconds,
                        },
                    },
                ],
                rows: {
                    getRowClass: (params) => {
                        const item = params.node.data;

                        if (item && this.producerId === item.producerId && item.isAllowedForPayout) {
                            return "highlight";
                        }
                    },
                },
            })
        );

        this.trackGridStore = new MrbTableViewStore(this.rootStore, this.queryUtility, {
            columns: [
                {
                    key: "trackTitle",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.TRACK",
                },
                {
                    key: "trackDuration",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.TRACK_DURATION",
                    format: (value) => convertSecondsToHMS(value),
                },
                {
                    key: "producerPlaybacks",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.DURATION_FOR_PAYOUT",
                    format: (value) => convertSecondsToHMS(value),
                },
                {
                    key: "producerPlaybacks",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.POINTS_FOR_PAYOUT",
                },
                {
                    key: "producerPayment",
                    title: "COMMON.PRODUCER.REPORT.COLUMNS.PRODUCER_PAYMENT",
                    format: (value) =>
                        currencyFormatter.format(value, {
                            currency: "USD",
                        }),
                },
            ],
        });

        this.trackListenerGridStore = new MrbTableViewStore(this.rootStore, this.queryUtility, {
            columns: [
                {
                    key: "userId",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.LISTENER",
                    format: (value) => {
                        if (this.producerReportStore.maskUserNames) {
                            return (
                                "Listener " +
                                (this.queryUtility.filter.pageSize * (this.queryUtility.filter.pageNumber - 1) +
                                    (findIndex(this.trackListenerGridStore.data, { userId: value }) + 1))
                            );
                        } else {
                            const item = find(this.trackListenerGridStore.data, { userId: value });
                            return (item?.user?.firstName ?? "Listener") + " " + (item?.user?.lastName ?? "");
                        }
                    },
                },
                {
                    key: "trackDuration",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.TRACK_DURATION",
                    format: (value) => convertSecondsToHMS(value),
                },
                {
                    key: "producerPlaybacks",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.DURATION_FOR_PAYOUT",
                    format: (value) => convertSecondsToHMS(value),
                },
                {
                    key: "producerPlaybacks",
                    title: "ADMINISTRATION.PRODUCER.PAYOUT_USER_EVENT.LIST.COLUMNS.POINTS_FOR_PAYOUT",
                },
                {
                    key: "producerPayment",
                    title: "COMMON.PRODUCER.REPORT.COLUMNS.PRODUCER_PAYMENT",
                    format: (value) =>
                        currencyFormatter.format(value, {
                            currency: "USD",
                        }),
                },
            ],
            rows: {
                getRowClass: (params) => {
                    const item = params.node.data;

                    if (item && this.producerId === item.producerId && item.isAllowedForPayout) {
                        return "highlight";
                    }
                },
            },
        });

        this.reaction(
            () => this.isCollapsed,
            (isCollapsed) => {
                if (isCollapsed) {
                    this.queryUtility.initialize();
                }
            }
        );

        this.reaction(
            () => this.producerReportStore.selectedReportViewId,
            (value) => {
                this.selectedReportViewId = value;
            }
        );
    }

    onInit() {
        return Promise.resolve();
    }

    async fetchResources(filter) {
        if (this.selectedReportViewId === 0) {
            this.gridStore.suspend();
            try {
                const response = await this.find(filter);
                runInAction(() => {
                    this.gridStore.setData(response);
                    this.gridStore.resume();
                });
            } catch (err) {
                this.gridStore.resume();
                this.onFetchError(err);
            }
        } else if (this.selectedReportViewId === 1) {
            this.trackGridStore.suspend();
            try {
                const response = await this.find(filter);
                runInAction(() => {
                    this.trackGridStore.setData(response);
                    this.trackGridStore.resume();
                });
            } catch (err) {
                this.trackGridStore.resume();
                this.onFetchError(err);
            }
        } else if (this.selectedReportViewId === 2) {
            this.trackListenerGridStore.suspend();
            try {
                const response = await this.find(filter);
                runInAction(() => {
                    this.trackListenerGridStore.setData(response);
                    this.trackListenerGridStore.resume();
                });
            } catch (err) {
                this.trackListenerGridStore.resume();
                this.onFetchError(err);
            }
        }
    }

    async find(params) {
        params.producerId = this.rootStore.routerStore.routerState.params.id;
        params.from = this.producerReportStore.queryUtility.filter.from;
        params.to = this.producerReportStore.queryUtility.filter.to;
        if (this.selectedReportViewId === 0) {
            params.userId = this.item.userId;
            params.from = this.item.subscriptionCycleStartDate;
            params.to = this.item.subscriptionCycleEndDate;
            let result = await this.routeStore.findProducerReportPayoutUserEvents(params);
            if (
                this.producerReportStore.maskUserNames &&
                result &&
                result.item &&
                Array.isArray(result.item) &&
                result.item.length > 0 &&
                isNil(result.item[0].device?.deviceName)
            ) {
                forEach(result.item, (item) => {
                    // Older/unresolved events have no embedded device; leave them untouched rather than deref null.
                    if (isNil(item.device)) {
                        return;
                    }
                    const deviceId = item.device.deviceId;
                    let index = this.itemDeviceIds.indexOf(deviceId);
                    if (index === -1) {
                        this.itemDeviceIds.push(deviceId);
                        index = this.itemDeviceIds.length - 1;
                    }
                    item.device.deviceName = "Device " + (index + 1);
                });
            }
            return result;
        } else if (this.selectedReportViewId === 1) {
            params.contentId = this.item.nakiNigunAlbumId ?? this.item.podcastId ?? this.item.stationId;
            return this.routeStore.findProducerReportTrackItems(params);
        } else if (this.selectedReportViewId === 2) {
            params.contentId = this.item.nakiNigunAlbumId ?? this.item.podcastId ?? this.item.stationId;
            params.trackId = this.item.trackId;
            return this.routeStore.findProducerReportTrackListeners(params);
        }
    }

    onFetchError(err) {
        this.rootStore.notificationStore.error(
            "An unexpected error occurred while trying to load data. Please contact the support team.",
            err
        );
    }

    @action.bound
    setUser(user) {
        this.user = user;
    }

    @action.bound
    setIsCollapsed(value) {
        this.isCollapsed = value;
    }
}

export default ProducerPayoutUserEventListViewStore;
